import React, { useCallback, useEffect, useState } from 'react';

import FormControl from 'components/FormControl';
import CurrencyInput from 'components/Inputs/CurrencyInput';
import RadioInput from 'components/Inputs/RadioInput';
import UnitOfMeasurementLegend from 'components/UnitOfMeasurementLegend';
import {
  I18N_AVANT_PLATFORM_LEASE_PROMPT_PATH,
  I18N_AVANT_PLATFORM_PROPERTY_LABEL_PATH,
  I18N_PLATFORM_COMMON_WORD_PATH,
} from 'constants/i18n';
import { IProperty } from 'interfaces/IProperty';
import { ITaxAndOpex } from 'interfaces/ITaxAndOpex';
import { translateText } from 'utils/i18n';
import { convertToNumber } from 'utils/parsers/convertToNumber';
import {
  currencyInputFormatter,
  getCurrencySymbol,
} from 'utils/formatters/currency';
import { getTaxAndOpexSuggestions } from 'utils/getTaxAndOpexSuggestions';

import { calculateTaxAndOpexTotal, getExpenseFromQuoteDate } from './utils';
import styles from '../../FormSection.module.scss';

interface Props {
  unitOfMeasurement: string;
  date: string | undefined;
  value: ITaxAndOpex;
  property: IProperty | undefined;
  onChange: (field: string, value: any) => void;
}

export const TaxAndOpEx: React.FC<Props> = ({
  value,
  unitOfMeasurement,
  property,
  onChange,
  date,
}) => {
  const [manuallyChanged, setManuallyChanged] = useState(false);

  const expenseRecord = getExpenseFromQuoteDate(date, property);
  const isRequired = !!expenseRecord;
  const isDisabled = !!value.taxAndOpex && !value.tax && !value.opex;
  const currency = getCurrencySymbol(property?.currencyCode);

  const onTaxAndOpexChange = (value: string) => {
    setManuallyChanged(true);
    onChange('taxAndOpex', value);
    onChange('taxAndOpexChangedManually', true);
    const hasValue = value.trim();
    if (hasValue) {
      onChange('tax', null);
      onChange('opex', null);
    }
  };

  const calculateTaxAndOpex = (tax?: number | null, opex?: number | null) => {
    const taxOpex = calculateTaxAndOpexTotal(tax, opex) || null;
    onChange('taxAndOpex', taxOpex);
    onChange('taxAndOpexChangedManually', true);
  };

  const updateOperatingExpenses = (newOpex: string) => {
    const tax = convertToNumber(value.tax);
    const opex = convertToNumber(newOpex);
    onChange('opex', newOpex);
    calculateTaxAndOpex(tax, opex);
  };

  const updateTax = (newTax: string) => {
    const tax = convertToNumber(newTax);
    const opex = convertToNumber(value.opex);
    onChange('tax', newTax);
    calculateTaxAndOpex(tax, opex);
  };

  const calculateTaxOpex = useCallback(() => {
    /**
     * **Automatically calculate taxOpex when all the following conditions are met:
     * - Manually has not changed
     * - Commencement date exists
     * - taxOpex is empty initially
     * - Expense record with taxAndOpex value exists
     * **/

    if (
      manuallyChanged ||
      !date ||
      value?.taxAndOpex ||
      !expenseRecord ||
      !expenseRecord?.taxAndOpex
    )
      return;

    onChange('taxAndOpex', expenseRecord.taxAndOpex);
  }, [date, expenseRecord, manuallyChanged, onChange, value?.taxAndOpex]);

  useEffect(() => {
    calculateTaxOpex();
  }, [calculateTaxOpex]);

  return (
    <>
      <FormControl
        label={translateText('avantPlatform.attributes.lease.label.taxAndOpex')}
        wrapperClassName={styles['form-row']}
        required={isRequired}
      >
        <div className={styles['large-input-container']}>
          <CurrencyInput
            currencySymbol={currency}
            iconPosition="left"
            id="tax-and-opex"
            onChange={onTaxAndOpexChange}
            formatter={currencyInputFormatter}
            placeholder={translateText(
              `${I18N_PLATFORM_COMMON_WORD_PATH}.enter`,
            )}
            value={value.taxAndOpex}
            isRequired={isRequired}
          />
          <UnitOfMeasurementLegend unitOfMeasurement={unitOfMeasurement} />
          <RadioInput
            selectedItem={Number(value.taxAndOpex)}
            data={getTaxAndOpexSuggestions(currency)}
            onChange={item => onTaxAndOpexChange(String(item?.value) || '')}
          />
        </div>
      </FormControl>
      <FormControl
        label={translateText(`${I18N_AVANT_PLATFORM_PROPERTY_LABEL_PATH}.opex`)}
        wrapperClassName={styles['form-row']}
        disabled={isDisabled}
      >
        <div className={styles['large-input-container']}>
          <CurrencyInput
            currencySymbol={currency}
            iconPosition="left"
            id="lease-opex"
            isDisabled={isDisabled}
            onChange={updateOperatingExpenses}
            formatter={currencyInputFormatter}
            placeholder={translateText(
              `${I18N_AVANT_PLATFORM_LEASE_PROMPT_PATH}.enterQuantity`,
            )}
            value={value.opex}
          />
          <UnitOfMeasurementLegend
            unitOfMeasurement={unitOfMeasurement}
            disabled={isDisabled}
          />
        </div>
      </FormControl>
      <FormControl
        label={translateText(
          `${I18N_AVANT_PLATFORM_PROPERTY_LABEL_PATH}.taxes`,
        )}
        wrapperClassName={styles['form-row']}
        disabled={isDisabled}
      >
        <div className={styles['large-input-container']}>
          <CurrencyInput
            currencySymbol={currency}
            iconPosition="left"
            id="lease-tax"
            isDisabled={isDisabled}
            onChange={updateTax}
            formatter={currencyInputFormatter}
            placeholder={translateText(
              `${I18N_AVANT_PLATFORM_LEASE_PROMPT_PATH}.enterQuantity`,
            )}
            value={value.tax}
          />
          <UnitOfMeasurementLegend
            unitOfMeasurement={unitOfMeasurement}
            disabled={isDisabled}
          />
        </div>
      </FormControl>
    </>
  );
};
